import { ChevronDown, Copy, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsPixelList,
  type FacebookAdsPixelListResult,
} from 'sprites-types/workflow/tools/facebookAdsPixelList'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FacebookAdsPixelListDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const schema = getToolResultSchema(facebookAdsPixelList, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as FacebookAdsPixelListResult
  const pixels = result.pixels || []

  if (pixels.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <FaFacebook className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No pixels found</div>
        </div>
      </div>
    )
  }

  // Extract ad account ID from workflow state if available
  const inputs = nodeExecution.inputs.functionArgs as Record<string, unknown> | undefined
  const workflowState = inputs?.workflowState as Record<string, unknown> | undefined
  const credentials = workflowState?.accountCredentials as Record<string, any> | undefined
  const adAccountId = credentials?.facebook?.selectedAdAccountId as string | undefined

  // Header context menu actions
  const headerActions: ContextAction[] = [
    ...(adAccountId
      ? [
          {
            id: 'open-events-manager',
            label: 'Open Events Manager',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () =>
              window.open(
                `https://business.facebook.com/events_manager2/list/pixel?act=${adAccountId}`,
                '_blank',
                'noopener,noreferrer'
              ),
          },
        ]
      : []),
    {
      id: 'copy-all-ids',
      label: 'Copy All Pixel IDs',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(pixels.map((p) => p.id).join('\n')),
    },
  ]

  return (
    <div className="space-y-3">
      {/* Collapsible header */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <FaFacebook className="h-5 w-5 text-[#0064e0]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Facebook Pixels</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {pixels.length} pixel{pixels.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual pixels - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {pixels.map((pixel, idx) => {
            const contextActions: ContextAction[] = [
              {
                id: 'copy-id',
                label: 'Copy Pixel ID',
                icon: <Copy className="h-4 w-4" />,
                onClick: () => navigator.clipboard.writeText(pixel.id),
              },
            ]

            return (
              <ContextMenuWrapper key={idx} actions={contextActions}>
                <div className="mx-8 rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
                  <div className="flex items-start gap-4 px-4 py-3">
                    <div className="mt-1 flex-shrink-0">
                      <div className="rounded-full bg-blue-100 p-2">
                        <FaFacebook className="h-5 w-5 text-[#0064e0]" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 text-sm font-semibold text-gray-900">{pixel.name}</div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                        <div className="text-xs text-gray-500">Pixel ID: {pixel.id}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </ContextMenuWrapper>
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadFacebookAdsPixelList(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsPixelListToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsPixelList, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsPixelListResult
  const pixels = result.pixels || []

  if (pixels.length === 0) {
    return '## Facebook Pixels\n\nNo pixels found.\n\n---\n'
  }

  let markdown = `## Facebook Pixels\n\n`
  markdown += `Found ${pixels.length} pixel${pixels.length !== 1 ? 's' : ''}:\n\n`

  pixels.forEach((pixel) => {
    markdown += `### ${pixel.name}\n`
    markdown += `- **Pixel ID:** ${pixel.id}\n\n`
  })

  markdown += '---\n'

  return markdown
}

export const FacebookAdsPixelListDisplay = createWorkflowToolComponent(
  FacebookAdsPixelListDisplayComponent,
  downloadFacebookAdsPixelList,
  facebookAdsPixelListToMarkdown
)
